import { useCallback, useRef, useState } from 'react';
import GlobalSearchService from 'services/GlobalSearch.service';

/**
 * Hook personalizado para gerenciar busca global
 *
 * Este hook utiliza as seguintes propriedades de contagem específica retornadas pela API:
 * - totalTicketCount: Quantidade total de tickets encontrados
 * - totalContactCount: Quantidade total de contatos encontrados
 * - totalArticleCount: Quantidade total de artigos encontrados
 *
 * @param {Object} searchSettings - Configurações de busca do usuário
 * @returns {Object} Objeto contendo funções e estado da busca
 */
const useGlobalSearch = searchSettings => {
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);

  const MIN_SEARCH_LENGTH = 4;
  const DEBOUNCE_DELAY = 300;

  /**
   * Realizar busca com debounce (usado no dropdown)
   * @param {string} query - Termo de busca
   * @param {number} page - Página atual
   * @param {number} size - Tamanho da página
   * @param {string} entityScope - Escopo da entidade
   * @param {Object} customSettings - Configurações customizadas (opcional)
   */
  const performSearch = useCallback(
    async (
      query,
      page = 0,
      size = 10,
      entityScope = 'all',
      customSettings = null
    ) => {
      if (
        !query ||
        query.trim().length < MIN_SEARCH_LENGTH ||
        (!searchSettings && !customSettings)
      ) {
        setSearchResults(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Usar configurações customizadas se fornecidas, senão usar as padrão
        const settingsToUse = customSettings || searchSettings;

        const searchBody = {
          query: query.trim(),
          searchIn: {
            searchInSubject: settingsToUse.searchInSubject,
            searchInDescription: settingsToUse.searchInDescription,
            searchInProperties: settingsToUse.searchInProperties,
            searchInNotes: settingsToUse.searchInNotes,
            searchInAttachments: settingsToUse.searchInAttachments
          },
          entityScope,
          page,
          size
        };

        const results = await GlobalSearchService.search(searchBody);
        setSearchResults(results);
      } catch (err) {
        console.error('Erro na busca global:', err);
        setError('Erro ao realizar a busca. Tente novamente.');
        setSearchResults(null);
      } finally {
        setIsLoading(false);
      }
    },
    [searchSettings]
  );

  /**
   * Realizar busca específica por tipo (usado na página de resultados)
   * @param {string} query - Termo de busca
   * @param {string} type - Tipo de entidade (tickets, contacts, articles)
   * @param {number} page - Página atual
   * @param {number} size - Tamanho da página
   */
  const performSpecificSearch = useCallback(
    async (query, type, page = 0, size = 20) => {
      if (
        !query ||
        query.trim().length < MIN_SEARCH_LENGTH ||
        !searchSettings
      ) {
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const searchBody = {
          query: query.trim(),
          searchIn: {
            searchInSubject: searchSettings.searchInSubject,
            searchInDescription: searchSettings.searchInDescription,
            searchInProperties: searchSettings.searchInProperties,
            searchInNotes: searchSettings.searchInNotes,
            searchInAttachments: searchSettings.searchInAttachments
          },
          page,
          size
        };

        let results;
        switch (type) {
          case 'tickets':
            results = await GlobalSearchService.searchTickets(searchBody);
            break;
          case 'contacts':
            results = await GlobalSearchService.searchContacts(searchBody);
            break;
          case 'articles':
            results = await GlobalSearchService.searchArticles(searchBody);
            break;
          default:
            throw new Error('Tipo de busca inválido');
        }

        return results;
      } catch (err) {
        console.error(`Erro na busca de ${type}:`, err);
        setError(`Erro ao buscar ${type}. Tente novamente.`);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [searchSettings]
  );

  /**
   * Busca com debounce para evitar muitas requisições
   * @param {string} query - Termo de busca
   * @param {number} page - Página atual
   * @param {number} size - Tamanho da página
   * @param {string} entityScope - Escopo da entidade
   */
  const debouncedSearch = useCallback(
    (
      query,
      page = 0,
      size = 10,
      entityScope = 'all',
      customSettings = null
    ) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query, page, size, entityScope, customSettings);
      }, DEBOUNCE_DELAY);
    },
    [performSearch]
  );

  /**
   * Limpar resultados da busca
   */
  const clearResults = useCallback(() => {
    setSearchResults(null);
    setError(null);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  /**
   * Calcular total de resultados
   * @returns {number} Total de resultados
   */
  const getTotalResults = useCallback(() => {
    if (!searchResults) return 0;

    // Verificar se é resultado do endpoint /search (pré-visualização)
    if (
      searchResults.tickets ||
      searchResults.customers ||
      searchResults.contacts ||
      searchResults.articles
    ) {
      // Usar as propriedades específicas de contagem se disponíveis
      if (
        searchResults.totalTicketCount !== undefined ||
        searchResults.totalContactCount !== undefined ||
        searchResults.totalArticleCount !== undefined
      ) {
        return (
          (searchResults.totalTicketCount || 0) +
          (searchResults.totalContactCount || 0) +
          (searchResults.totalArticleCount || 0)
        );
      }

      // Fallback para o cálculo manual (mantido para compatibilidade)
      return (
        (searchResults.tickets ? searchResults.tickets.length : 0) +
        (searchResults.customers ? searchResults.customers.length : 0) +
        (searchResults.contacts ? searchResults.contacts.length : 0) +
        (searchResults.articles ? searchResults.articles.length : 0)
      );
    }

    // Verificar se é resultado de endpoint específico (página de resultados)
    if (searchResults.totalElements !== undefined) {
      return searchResults.totalElements;
    }

    return 0;
  }, [searchResults]);

  /**
   * Obter contagem por tipo de entidade
   * @param {string} type - Tipo de entidade
   * @returns {number} Contagem de resultados
   */
  const getResultsCount = useCallback(
    type => {
      if (!searchResults) return 0;

      // Verificar se é resultado do endpoint /search (pré-visualização)
      if (searchResults[type] && Array.isArray(searchResults[type])) {
        // Usar as propriedades específicas de contagem se disponíveis
        switch (type) {
          case 'tickets':
            return searchResults.totalTicketCount !== undefined
              ? searchResults.totalTicketCount
              : searchResults.tickets.length;
          case 'contacts':
            return searchResults.totalContactCount !== undefined
              ? searchResults.totalContactCount
              : searchResults.contacts.length;
          case 'articles':
            return searchResults.totalArticleCount !== undefined
              ? searchResults.totalArticleCount
              : searchResults.articles.length;
          default:
            return searchResults[type].length;
        }
      }

      // Verificar se é resultado de endpoint específico (página de resultados)
      if (searchResults.content && Array.isArray(searchResults.content)) {
        return searchResults.content.length;
      }

      return 0;
    },
    [searchResults]
  );

  /**
   * Verificar se há resultados
   * @returns {boolean} True se há resultados
   */
  const hasResults = useCallback(() => {
    return searchResults && getTotalResults() > 0;
  }, [searchResults, getTotalResults]);

  /**
   * Verificar se a busca está vazia
   * @param {string} query - Termo de busca
   * @returns {boolean} True se a busca está vazia
   */
  const isEmptySearch = useCallback(query => {
    return !query || query.trim().length < MIN_SEARCH_LENGTH;
  }, []);

  /**
   * Obter contagens específicas por tipo de entidade
   * @returns {Object} Objeto com contagens específicas
   */
  const getSpecificCounts = useCallback(() => {
    if (!searchResults) {
      return {
        totalTicketCount: 0,
        totalContactCount: 0,
        totalArticleCount: 0
      };
    }

    // Verificar se é resultado do endpoint /search (pré-visualização)
    if (
      searchResults.tickets ||
      searchResults.contacts ||
      searchResults.articles
    ) {
      return {
        totalTicketCount: searchResults.totalTicketCount || 0,
        totalContactCount: searchResults.totalContactCount || 0,
        totalArticleCount: searchResults.totalArticleCount || 0
      };
    }

    // Para endpoints específicos, retornar contagem baseada no tipo
    return {
      totalTicketCount: 0,
      totalContactCount: 0,
      totalArticleCount: 0
    };
  }, [searchResults]);

  return {
    // Estado
    searchResults,
    isLoading,
    error,

    // Funções
    performSearch,
    performSpecificSearch,
    debouncedSearch,
    clearResults,
    getTotalResults,
    getResultsCount,
    getSpecificCounts,
    hasResults,
    isEmptySearch,

    // Constantes
    MIN_SEARCH_LENGTH,
    DEBOUNCE_DELAY
  };
};

export default useGlobalSearch;
